import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Enhanced ScrollAnimation component that supports both Framer Motion variants
// and CSS class-based animations for more complex animations
const ScrollAnimation = ({ 
  children, 
  variants, 
  threshold = 0.3, 
  transition, 
  className = "",
  customTrigger = false,
  once = true,
  ...props 
}) => {
  const elementRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: once,
    threshold,
    rootMargin: '-40px 0px' // Adjusted to trigger animations slightly earlier
  });

  // Default animation variants
  const defaultVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: transition || {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // For custom CSS animations - adds/removes "in-view" class
  useEffect(() => {
    if (customTrigger && elementRef.current) {
      if (inView) {
        // Add a small delay to make sequential animations more noticeable
        setTimeout(() => {
          if (elementRef.current) {
            elementRef.current.classList.add('in-view');
          }
        }, 50);
      } else if (!once) {
        elementRef.current.classList.remove('in-view');
      }
    }
  }, [inView, customTrigger, once]);

  // Merge the refs to handle both framer motion animation and class-based animation
  const setRefs = (element) => {
    // Set the ref for useInView
    ref(element);
    // Set our local ref
    elementRef.current = element;
  };

  // If customTrigger is true, we're using CSS classes for animation
  // Otherwise, use Framer Motion variants
  if (customTrigger) {
    return (
      <div
        ref={setRefs}
        className={className}
        {...props}
      >
        {children}
      </div>
    );
  }

  // Use Framer Motion animation
  const animationVariants = variants || defaultVariants;
  return (
    <motion.div
      ref={setRefs}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={animationVariants}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default ScrollAnimation;